import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AttendanceProvider } from './contexts/AttendanceContext';
import { RequestsProvider } from './contexts/RequestsContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Toaster } from './components/ui/sonner';
import InstallPWA from './components/InstallPWA';

import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Attendance from './pages/Attendance';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import Requests from './pages/Requests';
import Layout from './components/layout/Layout';

function ProtectedRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'hsl(32 38% 97%)' }}>
        <img 
          src="/logo-only.png" 
          alt="Loading..." 
          className="h-24 w-24 object-contain animate-pulse drop-shadow-md" 
        />
      </div>
    );
  }
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function PublicRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'hsl(32 38% 97%)' }}>
        <img 
          src="/logo-only.png" 
          alt="Loading..." 
          className="h-24 w-24 object-contain animate-pulse drop-shadow-md" 
        />
      </div>
    );
  }
  return !isAuthenticated ? <>{children}</> : <Navigate to="/" replace />;
}

function AppRoutes(): React.JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="attendance" element={<Attendance />} />
        <Route path="requests" element={<Requests />} />
        <Route path="settings" element={<Settings />} />
        <Route path="profile" element={<Profile />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App(): React.JSX.Element {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <AttendanceProvider>
            <RequestsProvider>
              <AppRoutes />
              <InstallPWA />
              <Toaster position="top-right" richColors />
            </RequestsProvider>
          </AttendanceProvider>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
